"use strict";
/**
 * Short history of the asks the client has actually RENDERED, so an order can
 * be priced off what the operator was looking at when they decided — not off
 * what the screen showed when their finger finally landed.
 *
 * The gap is reaction time: prices refresh every 125ms, but the order is built
 * in the click handler, several refreshes after the decision. Reading the
 * newest value there reports a price the operator never acted on.
 *
 * This works where the old server-side lookback did not, for three reasons:
 * samples arrive at a fixed cadence (polling, not book churn, so N slots really
 * is N × 125ms of coverage), each timestamp is the moment a value was displayed,
 * and the lookback is now a purely human constant rather than human plus
 * variable network latency.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HISTORY_SPAN_MS = exports.SEEN_ASK_LOOKBACK_MS = void 0;
exports.recordDisplayedAsks = recordDisplayedAsks;
exports.getDisplayedAsk = getDisplayedAsk;
exports.resetDisplayedAskHistory = resetDisplayedAskHistory;
exports.displayedAskHistorySize = displayedAskHistorySize;
const polling_service_1 = require("../services/polling-service");
/** How far back to look for the price the operator acted on. */
exports.SEEN_ASK_LOOKBACK_MS = 750;
/**
 * Slots to keep. Covers ~2s at the polling cadence — comfortably past the
 * lookback, and past the server's own staleness bound, so a reported sample is
 * never rejected merely for sitting in here too long.
 */
const CAPACITY = 16;
/**
 * Which market these samples belong to. A mismatch on read returns nothing
 * rather than another market's price — which is what makes a fanned-out order
 * on a different ticker fall back to the live book instead of being priced off
 * whatever happens to be on screen.
 */
let marketKey = null;
let head = 0;
let count = 0;
const ring = Array.from({ length: CAPACITY }, () => ({
    t: 0,
    yesAsk: null,
    noAsk: null,
}));
/** Record the asks just rendered for `marketId`. O(1), no allocation. */
function recordDisplayedAsks(marketId, yesAsk, noAsk, timestamp) {
    if (marketId !== marketKey) {
        marketKey = marketId;
        head = 0;
        count = 0;
    }
    const slot = ring[head];
    slot.t = timestamp;
    slot.yesAsk = yesAsk;
    slot.noAsk = noAsk;
    head = (head + 1) % CAPACITY;
    if (count < CAPACITY)
        count += 1;
}
/**
 * The ask displayed for `side` about `lookbackMs` ago, with its real age.
 *
 * Returns the newest sample at or before the target instant — what was on
 * screen then, not whichever timestamp is arithmetically nearest. Before enough
 * history accumulates, returns the oldest sample there is; null when the market
 * doesn't match or nothing usable has been recorded.
 */
function getDisplayedAsk(marketId, side, lookbackMs = exports.SEEN_ASK_LOOKBACK_MS, now = Date.now()) {
    if (marketId !== marketKey || count === 0) {
        return null;
    }
    const target = now - lookbackMs;
    let best = null;
    for (let i = 1; i <= count; i += 1) {
        const slot = ring[(head - i + CAPACITY) % CAPACITY];
        const price = side === "yes" ? slot.yesAsk : slot.noAsk;
        if (price === null || price <= 0) {
            continue;
        }
        best = { price, ageMs: Math.max(0, now - slot.t) };
        if (slot.t <= target) {
            return best;
        }
    }
    return best;
}
/** Drop all history (market switch, logout, tests). */
function resetDisplayedAskHistory() {
    marketKey = null;
    head = 0;
    count = 0;
}
/** Samples currently held — diagnostics and tests. */
function displayedAskHistorySize() {
    return count;
}
/** Coverage the ring can hold, for sanity-checking the lookback against it. */
exports.HISTORY_SPAN_MS = CAPACITY * polling_service_1.ORDER_POLLING_INTERVAL_MS;
